import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "@/hooks/useOnboarding";
import { UsernameStep } from "./UsernameStep";
import { PlanSelectionStep } from "./PlanSelectionStep";
import { LocationStep } from "./LocationStep";
import { GarageStep } from "./GarageStep";
import { GettingStartedStep } from "./GettingStartedStep";
import { Progress } from "@/components/ui/progress";
import { ROUTES } from "@/lib/routes";
import { useToast } from "@/hooks/use-toast";

const TOTAL_STEPS = 5;

export const OnboardingWizard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { completeOnboarding, isCompleting } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(1);

  const handleNextStep = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleComplete = async () => {
    try {
      await completeOnboarding();
      toast({
        title: "Welcome to Airing Down!",
        description: "You're all set to start exploring.",
      });
      navigate(ROUTES.activity);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast({
        title: "Error",
        description: "Failed to complete onboarding. Please try again.",
        variant: "destructive",
      });
    }
  };

  const progressPercent = (currentStep / TOTAL_STEPS) * 100;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <UsernameStep onNext={handleNextStep} />;
      case 2:
        return <PlanSelectionStep onNext={handleNextStep} />;
      case 3:
        return <LocationStep onNext={handleNextStep} />;
      case 4:
        return <GarageStep onNext={handleNextStep} />;
      case 5:
        return (
          <GettingStartedStep 
            onComplete={handleComplete} 
            isLoading={isCompleting} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with progress */}
      <div className="border-b bg-card">
        <div className="container max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold flex items-center gap-2">
              Welcome to Airing Down
              <span className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full font-medium">BETA</span>
            </h1>
            <span className="text-sm text-muted-foreground">
              Step {currentStep} of {TOTAL_STEPS}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-xl">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};
